import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Users, 
  CheckCircle2, 
  XCircle, 
  TrendingUp,
  Building2,
  Calendar,
  Send,
  BarChart3,
  FileCheck,
  FileX
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if it's a single-day view
const isSingleDayView = (daysValue) => daysValue === "1" || daysValue === "yesterday";

export default function DashboardPage() {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");
  const [sendingReminders, setSendingReminders] = useState({});

  const fetchStats = async (daysParam) => {
    try {
      setLoading(true);
      let url = `${API}/dashboard/stats?days=1`;
      
      if (daysParam === "yesterday") {
        // Use backend to calculate last business day (skips weekends)
        url = `${API}/dashboard/stats?days=1&use_last_business_day=true`;
      } else if (daysParam === "1") {
        // Today - just days=1
        url = `${API}/dashboard/stats?days=1`;
      } else {
        // Multi-day views
        url = `${API}/dashboard/stats?days=${daysParam}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(days);
  }, [days]);

  const sendTeamReminders = async (teamId, missingUsers) => {
    if (!missingUsers || missingUsers.length === 0) return;
    
    setSendingReminders(prev => ({ ...prev, [teamId]: true }));
    try {
      const userIds = missingUsers.map(u => u.id);
      await axios.post(`${API}/admin/send-reminders`, {
        log_date: stats.summary.date,
        user_ids: userIds
      });
      toast.success(`Reminders sent to ${missingUsers.length} users`);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(prev => ({ ...prev, [teamId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Failed to load dashboard data</p>
        <Button onClick={fetchStats} className="mt-4">Retry</Button>
      </div>
    );
  }

  const { summary, team_stats, daily_trend } = stats;

  // Calculate max for chart scaling
  const maxSubmissions = Math.max(...daily_trend.map(d => d.total), 1);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Team performance overview</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px]" data-testid="days-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Teams</p>
                <p className="text-3xl font-bold mt-1">{summary.total_teams}</p>
              </div>
              <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Members</p>
                <p className="text-3xl font-bold mt-1">{summary.total_members}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{days === "yesterday" ? "Yesterday" : "Today"} Submitted</p>
                <p className="text-3xl font-bold mt-1 text-green-600">{summary.today_submitted}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{days === "yesterday" ? "Yesterday" : "Today"} Missing</p>
                {summary.today_missing > 0 ? (
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <p className="text-3xl font-bold mt-1 text-red-600 cursor-pointer hover:underline">
                        {summary.today_missing}
                      </p>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-72">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">Missing Users ({summary.today_missing})</h4>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {summary.missing_users && summary.missing_users.map(user => (
                            <div key={user.id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-slate-500 text-xs">{user.email}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : (
                  <p className="text-3xl font-bold mt-1 text-red-600">{summary.today_missing}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Stats - Show when not single-day view (Today or Yesterday) */}
      {!isSingleDayView(days) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">Period Total Submitted</p>
                  <p className="text-3xl font-bold mt-1 text-emerald-600">{summary.period_total_submitted || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">of {summary.period_total_expected || 0} expected ({summary.period_submission_rate || 0}%)</p>
                </div>
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <FileCheck className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Period Total Missed</p>
                  <p className="text-3xl font-bold mt-1 text-red-600">{summary.period_total_missed || 0}</p>
                  <p className="text-xs text-red-600 mt-1">{summary.period_total_expected > 0 ? (100 - (summary.period_submission_rate || 0)).toFixed(1) : 0}% missed rate</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                  <FileX className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Period Submission Rate</p>
                  <p className="text-3xl font-bold mt-1 text-blue-600">{summary.period_submission_rate || 0}%</p>
                  <p className="text-xs text-blue-600 mt-1">Last {days} days (weekdays only)</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overall Completion Rate */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {days === "yesterday" ? "Yesterday's" : "Today's"} Completion Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${summary.overall_rate}%` }}
              />
            </div>
            <span className="text-2xl font-bold text-indigo-600 min-w-[80px] text-right">
              {summary.overall_rate}%
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-2">
            {summary.today_submitted} of {summary.total_members} members have submitted {days === "yesterday" ? "yesterday" : "today"}
          </p>
        </CardContent>
      </Card>

      {/* Submission Trend Chart - Hide for single-day views */}
      {!isSingleDayView(days) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Submission Trend ({days} days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32 mt-4">
              {daily_trend.map((day, index) => (
                <div 
                  key={day.date} 
                  className="flex-1 flex flex-col items-center gap-1"
                >
                  <div className="w-full flex flex-col items-center justify-end h-24">
                    <span className="text-xs text-slate-500 mb-1">{day.submitted}</span>
                    <div 
                      className={`w-full rounded-t transition-all duration-300 ${
                        day.is_weekend ? 'bg-slate-200' : 'bg-indigo-500'
                      }`}
                      style={{ 
                        height: `${Math.max((day.submitted / maxSubmissions) * 100, 4)}%`,
                        minHeight: '4px'
                      }}
                    />
                  </div>
                  <span className={`text-xs ${day.is_weekend ? 'text-slate-400' : 'text-slate-600'}`}>
                    {day.day}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-indigo-500 rounded" />
                <span>Weekday</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-slate-200 rounded" />
                <span>Weekend</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Performance Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Team Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {team_stats.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No teams found. {isAdmin && "Create teams in the Teams page."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Team</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Members</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{isSingleDayView(days) ? (days === "yesterday" ? "Yesterday" : "Today") : "Submitted"}</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Missing</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Rate</th>
                    {!isSingleDayView(days) && <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{days}d Total</th>}
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{isSingleDayView(days) ? "Rate" : `${days}d Avg`}</th>
                    {isAdmin && <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {team_stats.map((team) => (
                    <tr key={team.team_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2">
                        <span className="font-medium">{team.team_name}</span>
                      </td>
                      <td className="py-3 px-2 text-center">{team.member_count}</td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-green-600 font-medium">
                          {isSingleDayView(days) ? team.today_submitted : team.period_submitted}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(isSingleDayView(days) ? team.today_missing : (team.period_total - team.period_submitted)) > 0 ? (
                          isSingleDayView(days) ? (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <button className="text-red-600 font-medium hover:underline cursor-pointer">
                                  {team.today_missing}
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-64">
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm">Missing Users</h4>
                                  <div className="max-h-40 overflow-y-auto space-y-1">
                                    {team.missing_users.map(user => (
                                      <div key={user.id} className="text-sm">
                                        <p className="font-medium">{user.full_name}</p>
                                        <p className="text-slate-500 text-xs">{user.email}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            <span className="text-red-600 font-medium">{team.period_total - team.period_submitted}</span>
                          )
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                (isSingleDayView(days) ? team.completion_rate : team.period_rate) >= 80 ? 'bg-green-500' :
                                (isSingleDayView(days) ? team.completion_rate : team.period_rate) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${isSingleDayView(days) ? team.completion_rate : team.period_rate}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12">{isSingleDayView(days) ? team.completion_rate : team.period_rate}%</span>
                        </div>
                      </td>
                      {!isSingleDayView(days) && (
                        <td className="py-3 px-2 text-center">
                          <span className="text-sm font-medium text-emerald-600">{team.period_submitted}</span>
                          <span className="text-xs text-slate-400">/{team.period_total}</span>
                        </td>
                      )}
                      <td className="py-3 px-2 text-center">
                        <span className="text-sm text-slate-600">{team.period_rate}%</span>
                      </td>
                      {isAdmin && (
                        <td className="py-3 px-2 text-center">
                          {team.today_missing > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendTeamReminders(team.team_id, team.missing_users)}
                              disabled={sendingReminders[team.team_id]}
                              data-testid={`send-reminder-${team.team_id}`}
                            >
                              {sendingReminders[team.team_id] ? (
                                <span className="spinner-sm" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
